import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { WorkDetailsModel } from "../../factories/work/model/work-details.model";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetWorkActivityQuery } from "../../slices/ActivityLogSlice";
import { useGetAutomationsQuery } from "../../slices/AutomationSlice";
import { useGetWorkFilesListQuery } from "../../slices/DocumentsSlice";
import { useGetWorkSequencesQuery } from "../../slices/EmailSequencesSlice";
import { useGetWorkEmailQuery } from "../../slices/EmailsSlice";
import { useFilterTimeQuery } from "../../slices/UserSlice";
import { useGetWorkNotesQuery } from "../../slices/WorkNotesSlice";
import { useGetWorkRemindersQuery } from "../../slices/WorkRemindersSlice";
import { useGetRepeatsWorkQuery } from "../../slices/WorkSlice";
import { listCount, TabCount } from "../../utils/tabCounts";
import { defaultTabTimeQueryArgs } from "../time/time-filter.utils";
import { WorkTableSort } from "../works/types/work-table";
import { repeatListRows } from "./pages/repeats/seriesStatus";

/** The work item's Log tab loads the newest 200 entries (backend default). */
const LOG_LIMIT = 200;

export type WorkTabCounts = {
  time: TabCount;
  repeats: TabCount;
  files: TabCount;
  notes: TabCount;
  emails: TabCount;
  sequences: TabCount;
  automation: TabCount;
  reminders: TabCount;
  log: TabCount;
};

/**
 * Counts for the work item's tabs (Roadmap 1842). Each count reads the same
 * data (same request) as its tab, so it matches the rows the tab shows and
 * updates as soon as the tab's data changes (upload, new note, ...).
 */
export const useWorkTabCounts = (
  workId: string,
  work?: WorkDetailsModel,
): WorkTabCounts => {
  const { user } = useAppSelector((state) => state.appReducer);
  const { pathname } = useLocation();
  const orgId = work?.orgId || getLinkedFirm()?.orgId || "";
  const skip = !workId;

  const { data: time } = useFilterTimeQuery(
    defaultTabTimeQueryArgs({ orgId, workId }),
    { skip: skip || !orgId },
  );
  // Same request as the Repeats tab with its default sort.
  const { data: repeats } = useGetRepeatsWorkQuery(
    {
      orgId: getLinkedFirm()?.orgId || "",
      workId,
      currentUserId: user?.id,
      sortBy: WorkTableSort.StartDate,
      sortOrder: "desc",
    },
    { skip: skip || !user?.id },
  );
  const { data: files } = useGetWorkFilesListQuery(workId, { skip });
  const { data: notes } = useGetWorkNotesQuery({ workId }, { skip });
  const { data: emails } = useGetWorkEmailQuery(
    { userId: user?.id || "", workId },
    { skip: skip || !user?.id },
  );
  const { data: sequences } = useGetWorkSequencesQuery({ workId }, { skip });
  const { data: automations } = useGetAutomationsQuery({ workId }, { skip });
  const { data: reminders } = useGetWorkRemindersQuery(
    { orgId, workId },
    { skip: skip || !orgId },
  );
  const { data: log, refetch: refetchLog } = useGetWorkActivityQuery(
    { workId },
    { skip },
  );

  // Log entries are written by the server after other changes, so the log is
  // re-read when anything else on the work item changes or another tab opens.
  const changeKey = [
    pathname,
    time?.totalCount,
    repeats?.length,
    files?.length,
    notes?.length,
    sequences?.length,
    automations?.workAutomations?.length,
    automations?.workTaskAutomations?.length,
    reminders?.length,
    work?.statusUpdatedAt,
    work?.name,
  ].join("|");
  useEffect(() => {
    if (!skip && log) refetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeKey]);

  return useMemo(
    () => ({
      time: time?.totalCount,
      // Same rows as the Repeats tab lists (repeatListRows), so badge = list.
      repeats: listCount(repeatListRows(repeats, workId)),
      files: listCount(files),
      notes: listCount(notes),
      emails: listCount(emails?.data),
      sequences: listCount(sequences),
      automation: automations
        ? (automations.workAutomations?.length || 0) +
          (automations.workTaskAutomations?.length || 0)
        : undefined,
      reminders: listCount(reminders),
      log: listCount(log, { cap: LOG_LIMIT }),
    }),
    [
      time,
      repeats,
      files,
      notes,
      emails,
      sequences,
      automations,
      reminders,
      log,
      workId,
    ],
  );
};
